/**
 * PlanetaryChartSnapshot — KALKI 5.1 structured planetary chart card.
 *
 * Rendered inline inside a Krishna chat message when the backend attaches
 * `planetary_card` to the chat response envelope (triggered by the
 * PLANETARY_OVERVIEW intent — "show all my planets" / "rank my planets").
 *
 * Two labelled views, strictly separated:
 *   • Birth Chart     — natal.sign · house · nakshatra · dignity · shadbala
 *   • Current Transits — on sign · house_from_lagna · house_from_moon
 *
 * Nothing is computed on the client — every value comes verbatim from the
 * Astro Monish engine via /api/vedic/calc. Rahu/Ketu rows are tagged
 * "Nodal — strength N/A" (chara-karaka role shown next to the planet name).
 */
import React, { useState } from "react";

const INDIGO = "#5D00FF";
const INK    = "#1C1C1C";
const MUTE   = "#676773";
const SOFT   = "#F6F2FF";

export default function PlanetaryChartSnapshot({ card }) {
  const [tab, setTab] = useState("natal");
  if (!card || typeof card !== "object") return null;
  const natal    = Array.isArray(card.natal) ? card.natal : [];
  const transits = Array.isArray(card.transits) ? card.transits : [];
  if (natal.length === 0 && transits.length === 0) return null;

  const strongest = (card.strongest || [])[0] || null;
  const weakest   = (card.weakest || [])[0] || null;
  const nodes     = (card.ranking && card.ranking.nodes) || [];
  const nodeRole  = {};
  nodes.forEach((n) => { nodeRole[n.planet] = n.karaka_role || "node"; });

  const header = (
    <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap",
                  marginBottom: 10 }} data-testid="planetary-card-header">
      <span style={{ fontSize: 11, letterSpacing: "0.3em", textTransform: "uppercase",
                     color: INDIGO }}>Planetary Snapshot</span>
      <span style={{ fontSize: 11, color: MUTE }}>
        Lagna <strong style={{ color: INK }}>{card.lagna || "?"}</strong>
        {" · "}
        Moon <strong style={{ color: INK }}>{card.moon || "?"}</strong>
        {" · "}
        Atmakaraka <strong style={{ color: INK }}>{card.atmakaraka || "?"}</strong>
      </span>
    </div>
  );

  const tabs = (
    <div style={{ display: "inline-flex", gap: 4, padding: 4, borderRadius: 999,
                  background: "rgba(93,0,255,0.06)", marginBottom: 10 }}
         role="tablist" data-testid="planetary-card-tabs">
      {["natal", "transits"].map((k) => (
        <button key={k}
          role="tab"
          aria-selected={tab === k}
          data-testid={`planetary-tab-${k}`}
          onClick={() => setTab(k)}
          style={{
            padding: "6px 14px", borderRadius: 999, fontSize: 11.5,
            letterSpacing: "0.18em", textTransform: "uppercase",
            border: "none", cursor: "pointer",
            background: tab === k ? INDIGO : "transparent",
            color: tab === k ? "white" : INK,
            transition: "background 160ms ease, color 160ms ease",
          }}>
          {k === "natal" ? "Birth Chart" : `Transits · ${card.on || "today"}`}
        </button>
      ))}
    </div>
  );

  const summary = (strongest || weakest) ? (
    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 10,
                  fontSize: 12, color: MUTE }} data-testid="planetary-card-summary">
      {strongest && (
        <span>Strongest: <strong style={{ color: INK }}>{strongest.planet}</strong>
          {" "}in {strongest.sign} H{strongest.house}
          {" "}(shadbala {typeof strongest.ratio === "number" ? strongest.ratio.toFixed(2) : "—"})</span>
      )}
      {weakest && (
        <span>Weakest: <strong style={{ color: INK }}>{weakest.planet}</strong>
          {" "}in {weakest.sign} H{weakest.house}
          {" "}(shadbala {typeof weakest.ratio === "number" ? weakest.ratio.toFixed(2) : "—"})</span>
      )}
    </div>
  ) : null;

  const tableNatal = (
    <div style={{ overflowX: "auto" }} data-testid="planetary-table-natal">
      <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ color: MUTE, fontSize: 10.5, letterSpacing: "0.18em",
                        textTransform: "uppercase" }}>
            <Th>Planet</Th><Th>Sign</Th><Th>House</Th>
            <Th>Nakshatra</Th><Th>Dignity</Th><Th>Strength</Th>
          </tr>
        </thead>
        <tbody>
          {natal.map((row) => {
            const n = row.natal || {};
            const strength = row.strength;
            const isNode = row.planet === "Rahu" || row.planet === "Ketu";
            const sv = (strength === "AVAILABLE" && typeof n.shadbala_ratio === "number")
              ? n.shadbala_ratio.toFixed(2)
              : (isNode ? `Nodal · ${nodeRole[row.planet] || "role"}` : "—");
            return (
              <tr key={row.planet} data-testid={`planetary-row-${row.planet.toLowerCase()}`}
                  style={{ borderTop: "1px solid rgba(0,0,0,0.06)" }}>
                <Td bold>{row.planet}
                  {n.retrograde && !isNode ? <sup style={{ color: INDIGO }}> R</sup> : null}
                  {n.combust ? <sup style={{ color: "#B91C1C" }}> C</sup> : null}
                </Td>
                <Td>{n.sign || "—"}</Td>
                <Td>H{n.house || "—"}</Td>
                <Td>{n.nakshatra || "—"}</Td>
                <Td style={{ color: n.dignity === "exalted" ? "#15803D"
                              : (n.dignity === "debilitated" ? "#B91C1C" : INK) }}>
                  {n.dignity || "neutral"}
                </Td>
                <Td style={{ color: isNode ? MUTE : INK }}>{sv}</Td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ marginTop: 8, fontSize: 10.5, color: MUTE }}>
        Rahu and Ketu are nodes; strength is assessed via their chara-karaka role,
        not Shadbala. All values are from the Astro Monish engine — nothing invented.
      </div>
    </div>
  );

  const tableTransits = (
    <div style={{ overflowX: "auto" }} data-testid="planetary-table-transits">
      <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ color: MUTE, fontSize: 10.5, letterSpacing: "0.18em",
                        textTransform: "uppercase" }}>
            <Th>Planet</Th><Th>Sign</Th>
            <Th>House · Lagna</Th><Th>House · Moon</Th><Th>Retro</Th>
          </tr>
        </thead>
        <tbody>
          {transits.map((row) => (
            <tr key={row.planet} data-testid={`planetary-row-transit-${row.planet.toLowerCase()}`}
                style={{ borderTop: "1px solid rgba(0,0,0,0.06)" }}>
              <Td bold>{row.planet}</Td>
              <Td>{row.sign || "—"}</Td>
              <Td>H{row.house_from_lagna || "—"}</Td>
              <Td>H{row.house_from_moon || "—"}</Td>
              <Td>{row.retrograde ? "Yes" : "—"}</Td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: 8, fontSize: 10.5, color: MUTE }}>
        Current sky as of {card.on || "today"}. Natal positions are separate — see the Birth Chart tab.
      </div>
    </div>
  );

  return (
    <div data-testid="planetary-chart-snapshot"
         style={{
           marginTop: 12, padding: 14, borderRadius: 16,
           background: SOFT, border: "1px solid rgba(93,0,255,0.14)",
         }}>
      {header}
      {summary}
      {tabs}
      {tab === "natal" ? tableNatal : tableTransits}
    </div>
  );
}

function Th({ children }) {
  return <th style={{ textAlign: "left", padding: "6px 10px", fontWeight: 500 }}>{children}</th>;
}
function Td({ children, bold, style }) {
  return (
    <td style={{ padding: "8px 10px", fontWeight: bold ? 600 : 400, color: INK, ...style }}>
      {children}
    </td>
  );
}
